<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-scroll flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-10/12 h-[65vh] overflow-y-auto">
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-2xl text-gray-800 font-bold">Edit Scheme</h4>
        <button
          type="button"
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-yellow-500"
          @click="handleCloseModal"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <X :size="24" />
        </button>
      </div>
      <form
        @submit.prevent="handleSubmitForm"
        class="space-y-4 p-6"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-6 px-4 md:px-20">

          <!-- Insurance -->
          <div>
            <label for="insurance" class="block text-sm font-medium text-gray-700">
              Insurance <span class="text-red-500">*</span>
            </label>
            <v-select
              class="mt-2"
              v-model="formData.insurance_id"
              :options="insurances"
              label="ProviderName"
              :reduce="insurance => insurance.InsuranceId"
              placeholder="Select an Insurance"
              :loading="isSearching"
            >
            </v-select>
            <span class="text-sm text-red-500" v-if="errors.insurance_id">{{ errors.insurance_id }}</span>
          </div>

          <!-- Code -->
          <div>
            <label for="code" class="block text-sm font-medium text-gray-700">
              Code <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="code"
              v-model="formData.code"
              placeholder="Enter code.."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.code">{{ errors.code }}</span>
          </div>

          <!-- Scheme Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">
              Scheme Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              placeholder="Enter scheme name.."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.name">{{ errors.name }}</span>
          </div>

          <!-- Status -->
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700">
              Status
            </label>
            <v-select
              class="mt-2"
              v-model="formData.status_id"
              :options="statuses"
              :reduce="status => status.id"
              label="name"
              placeholder="Select Scheme Status"
            >
            </v-select>
            <span class="text-sm text-red-500" v-if="errors.status_id">
              {{ errors.status_id }}
            </span>
          </div>

          <!-- Valid From -->
          <div>
            <label for="validFrom" class="block text-sm font-medium text-gray-700">
              Valid From
            </label>
            <input
              type="date"
              id="validFrom"
              v-model="formData.valid_from"
              placeholder="Select Start Date"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.valid_from">
              {{ errors.valid_from }}
            </span>
          </div>

          <!-- Valid To -->
          <div>
            <label for="validTo" class="block text-sm font-medium text-gray-700">
              Valid To
            </label>
            <input
              type="date"
              id="validTo"
              v-model="formData.valid_to"
              placeholder="Select End Date"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.valid_to">
              {{ errors.valid_to }}
            </span>
          </div>

          <!-- Scheme Type -->
          <div>
            <label for="schemeType" class="block text-sm font-medium text-gray-700">
              Scheme Type
            </label>
            <select
              id="schemeType"
              v-model.number="formData.scheme_type"
              @change="checkIfCapitation"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            >
              <option :value="null">Select Scheme Type</option>
              <option
                v-for="type in schemeTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
            <span class="text-sm text-red-500" v-if="errors.scheme_type">
              {{ errors.scheme_type }}
            </span>
          </div>

          <!-- Copay Type -->
          <div v-if="isCapitation">
            <label for="copayType" class="block text-sm font-medium text-gray-700">
              Copay Type
            </label>
            <select
              id="copayType"
              v-model.number="formData.copay_type"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            >
              <option :value="null">Select Copay Type</option>
              <option
                v-for="type in copayTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
            <span class="text-sm text-red-500" v-if="errors.copay_type">
              {{ errors.copay_type }}
            </span>
          </div>

          <!-- Copay Amount -->
          <div v-if="isCapitation">
            <label for="copayAmount" class="block text-sm font-medium text-gray-700">
              Copay Amount
            </label>
            <input
              type="number"
              id="copayAmount"
              v-model.number="formData.copay_amount"
              placeholder="Type..."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.copay_amount">
              {{ errors.copay_amount }}
            </span>
          </div>
        </div>

        <div class="flex justify-end mt-6 gap-y-6 px-4 md:px-20 pb-6">
          <button
            type="submit"
            :disabled="showLoader"
            class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
          >
            <span v-if="!showLoader"> Submit </span>
            <span v-else class="flex items-center">
              <svg
                class="animate-spin h-5 w-5 text-white mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                />
              </svg>
              Updating...
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, defineEmits } from "vue";
import vSelect from "vue-select";
import { X } from "lucide-vue-next"
import apiClient from "@/services/api"
import { useToast } from "vue-toast-notification"

const toast = useToast({
  position: 'top-right',
  duration: 3000,
  dismissible: true,
  pauseOnHover: true,
  type: 'default',
});

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  schemeItem: {
    type: Object,
    required: true
  }
});

const emits = defineEmits(['close', 'updated']);

// Form state
const formData = ref({
  id: '',
  code: '',
  name: '',
  insurance_id: '',
  valid_from: null,
  valid_to: null,
  scheme_type: null,
  copay_type: null,
  copay_amount:null,
  status_id: null,
});

// UI State
const showLoader = ref(false);
const isSearching = ref(false);
const isCapitation = ref(false);
const errors = ref({});


// Dropdown options
const insurances = ref([]);
const schemeTypes = ref([
  { id: 1, name: "Fee For Service (FFS)" },
  { id: 2, name: "Capitation" },
  { id: 3, name: "Cash" },
]);
const copayTypes = ref([
  { id: 1, name: "Percentage" },
  { id: 2, name: "Flat Rate" },
]);
const statuses = ref([]);

// Check if scheme type is Capitation to show copay fields
const checkIfCapitation = () => {
  console.log('checkIfCapitation called', formData.value.scheme_type)
  isCapitation.value = formData.value.scheme_type === 2;
  if (!isCapitation.value) {
    formData.value.copay_type = null;
    formData.value.copay_amount = null;
  }
};

// Utility to format date for input type="date"
const formatDateForInput = (dateString) => {
  console.log('date', dateString)
  if (!dateString) return null;
  const date = new Date(dateString)

  return date.toISOString().split('T')[0];
}

// Watch for changes in the schemeItem prop to populate the form
watch(() => props.schemeItem, (newItem) => {
  if (newItem) {
    formData.value = {
      id: newItem.id || '',
      code: newItem.code || '',
      name: newItem.name || '',
      insurance_id: newItem.insurance_id || '',
      valid_from: formatDateForInput(newItem.valid_from),
      valid_to: formatDateForInput(newItem.valid_to),
      scheme_type: newItem.scheme_type === undefined ? null : Number(newItem.scheme_type),
      copay_type: newItem.copay_type === undefined ? null : Number(newItem.copay_type),
      copay_amount: newItem.copay_amount === undefined ? null : Number(newItem.copay_amount),
      status_id: newItem.status_id === undefined ? null : newItem.status_id
    };
    checkIfCapitation();
  }
}, { immediate: true });


// Fetch insurance options
const fetchInsurances = async () => {

  try {
    isSearching.value = true;
    const response = await apiClient.getAllInsurances();

    if (response.success) {
      insurances.value = response.data
    } else {
      toast.error("Failed to fetch insurances: " + response.message);
    }
  } catch (error) {
    toast.error("Error fetching insurances");
    console.error("Error fetching insurances:", error);
  } finally {
    isSearching.value = false;
  }
};

// Fetch Statuses
const fetchStatuses = async () => {
  try {
    const response = await apiClient.fetchStatuses(); // Adjust based on actual API
    if (response.success) {
      statuses.value = response.data
    } else {
      toast.error("Failed to fetch statuses: " + response.message);
    }
  } catch (error) {
    toast.error("Error fetching statuses");
    console.error("Error fetching statuses:", error);
  }
};

// Form validation
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Required fields
  if (!formData.value.code || typeof formData.value.code !== "string" || !formData.value.code.trim()) {
    errors.value.code = "Code is required";
    isValid = false;
  } else if (/^\d+$/.test(formData.value.code.trim())) {
    errors.value.code = "Code cannot be purely numeric";
    isValid = false;
  }

  if (!formData.value.name || typeof formData.value.name !== "string" || !formData.value.name.trim()) {
    errors.value.name = "Scheme Name is required";
    isValid = false;
  } else if (/^\d+$/.test(formData.value.name.trim())) {
    errors.value.name = "Scheme Name cannot be purely numeric";
    isValid = false;
  }

  if (!formData.value.insurance_id) {
    errors.value.insurance_id = "Insurance is required";
    isValid = false;
  }

  if (!formData.value.scheme_type) {
    errors.value.scheme_type = "Scheme Type is required";
    isValid = false;
  }

  // Validate copay fields if scheme_type is Capitation (2)
  if (formData.value.scheme_type === 2) {
    if (!formData.value.copay_type) {
      errors.value.copay_type = "Copay Type is required for Capitation";
      isValid = false;
    }
    if (formData.value.copay_amount === null || formData.value.copay_amount === "" || formData.value.copay_amount < 0) {
      errors.value.copay_amount = "Copay Amount must be a positive number";
      isValid = false;
    }
  }

  // Optional date fields validation
  if (formData.value.valid_from && isNaN(new Date(formData.value.valid_from))) {
    errors.value.valid_from = "Valid From must be a valid date";
    isValid = false;
  }

  if (formData.value.valid_to && isNaN(new Date(formData.value.valid_to))) {
    errors.value.valid_to = "Valid To must be a valid date";
    isValid = false;
  }

  if (formData.value.valid_from && formData.value.valid_to) {
    const validFrom = new Date(formData.value.valid_from);
    const validTo = new Date(formData.value.valid_to);
    if (validFrom > validTo) {
      errors.value.valid_to = "Valid To must be after Valid From";
      isValid = false;
    }
  }

  return isValid;
};

const handleSubmitForm = async () => {
  if (!validateForm()) {
    toast.error("Please address the errors on the form.")
    return;
  }

  showLoader.value = true;

  console.log("form data", formData.value)

  try {
    const payload = {
      id: formData.value.id,
      code: formData.value.code,
      name: formData.value.name,
      insurance_id: formData.value.insurance_id,
      scheme_type: formData.value.scheme_type,
      copay_type: isCapitation.value ? formData.value.copay_type : null,
      copay_amount: isCapitation.value ? formData.value.copay_amount : null,
      status_id: formData.value.status_id,
      valid_from: formData.value.valid_from,
      valid_to: formData.value.valid_to
    };

    const response = await apiClient.updateScheme(payload.id, payload)

    if (response.success) {
      toast.success(response.message);
      handleSuccessfulUpdate();
      handleCloseModal();
    } else {
      toast.error("Failed to update scheme " + response.message)
    }
  } catch (error) {
    toast.error("Error: " + error.message)
    console.log("Error updating scheme", error)
  } finally {
    showLoader.value = false;
  }
}

const handleCloseModal = () => {
  emits('close');
};

const handleSuccessfulUpdate = () => {
  emits('updated')
};

onMounted(() => {
  fetchInsurances();
  fetchStatuses();
})

</script>
